import { useEffect } from 'react';

import { enqueueSnackbar } from 'notistack';

import {
  ApolloError,
  Context,
  DocumentNode,
  LazyQueryExecFunction,
  OperationVariables,
  QueryResult,
  TypedDocumentNode,
  useLazyQuery,
  WatchQueryFetchPolicy,
} from '@apollo/client';

export function usePrivateApollo<T>({
  bypassAuth = false,
  context,
  fetchPolicy = 'cache-and-network',
  onCompleted,
  onError,
  query,
  showErrors = true,
  skip,
  variables,
}: {
  bypassAuth?: boolean;
  context?: Context;
  fetchPolicy?: WatchQueryFetchPolicy;
  onCompleted?: (data: { data: T }) => void;
  onError?: (error: ApolloError) => void;
  query: DocumentNode | TypedDocumentNode<T, OperationVariables>;
  showErrors?: boolean;
  skip?: boolean;
  variables?: any;
}) {
  const [executeQuery, result] = useLazyQuery<{ data: T }>(query, {
    context: {
      ...context,
      bypassAuth,
    },
    fetchPolicy,
    onCompleted,
    onError,
    variables,
  });

  useEffect(() => {
    if (!skip) {
      executeQuery();
    }
  }, [skip, executeQuery]);

  useEffect(() => {
    const isExpired = result.error?.message.includes('timestamp check failed');
    const message =
      (result.error?.cause as any)?.result?.errors?.[0].message || result.error?.message;
    if (message && showErrors && !isExpired) {
      enqueueSnackbar(message.substring(0, 300), {
        autoHideDuration: 3000,
        variant: 'error',
      });
    }
  }, [result.error?.cause, result.error?.message, showErrors]);

  return {
    data: result?.data?.data,
    error: result.error,
    executeQuery,
    loading: result.loading,
    refetch: result?.refetch,
  } as QueryResult<T | undefined, OperationVariables> & {
    executeQuery: LazyQueryExecFunction<
      {
        data: T | undefined;
      },
      OperationVariables
    >;
  };
}
